import React, { useEffect, useMemo, useState } from 'react'
import { PlusIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { convertLeadToCustomer } from '../lib/leadConversion'
import {
  applyConversionDataToLead,
  cleanupInternalConversionCustomFieldsOnLead,
  emptyConversionData,
  emptyConversionProperty,
  populateConversionDataFromLead,
  conversionContactsToCustomerContacts,
  type ConversionData,
  type ConversionProperty,
} from '../lib/leadConversionForm'
import { CustomerCompanyFields } from './customer/CustomerCompanyFields'
import { CustomerContactsFields } from './customer/CustomerContactsFields'
import { CustomerManagementFields } from './customer/CustomerManagementFields'
import type { CustomerContact, UserProfile } from './customer/types'
import { PropertyAddressFields } from './property/PropertyAddressFields'
import { PropertyInfoFields } from './property/PropertyInfoFields'
import { PropertyPaymentFields } from './property/PropertyPaymentFields'
import { PropertyContactsFields } from './property/PropertyContactsFields'
import { PropertyNotesFields } from './property/PropertyNotesFields'
import { mobile } from './mobile'

const CUSTOMER_STEPS = ['company', 'contacts', 'management'] as const
const PROPERTY_TABS = ['address', 'info', 'payment', 'contacts', 'notes'] as const

type PropertyTab = (typeof PROPERTY_TABS)[number]

interface ConvertLeadModalProps {
  lead: any
  open: boolean
  onClose: () => void
  onSuccess: (customerId: string) => void | Promise<void>
  onError?: (message: string) => void
  variant?: 'default' | 'proposal'
  confirmLabel?: string
  serviceCategories?: Array<{ id: string; name: string }>
}

function getStepLabel(
  stepIndex: number,
  properties: ConversionProperty[]
): { title: string; subtitle?: string } {
  if (stepIndex < CUSTOMER_STEPS.length) {
    const step = CUSTOMER_STEPS[stepIndex]
    if (step === 'company') return { title: 'Company Information' }
    if (step === 'contacts') return { title: 'Contacts' }
    return { title: 'Notes & Management' }
  }

  const propertyBlockStart = CUSTOMER_STEPS.length
  const propertyBlockSize = PROPERTY_TABS.length
  const reviewIndex = propertyBlockStart + properties.length * propertyBlockSize

  if (stepIndex >= reviewIndex) {
    return { title: 'Review & Convert' }
  }

  const rel = stepIndex - propertyBlockStart
  const propIndex = Math.floor(rel / propertyBlockSize)
  const tab = PROPERTY_TABS[rel % propertyBlockSize]
  const prop = properties[propIndex]
  const tabLabels: Record<PropertyTab, string> = {
    address: 'Address',
    info: 'Building & Access',
    payment: 'Payment & Sales Tax',
    contacts: 'Property Contacts',
    notes: 'Notes',
  }
  return {
    title: `Property ${propIndex + 1} — ${tabLabels[tab]}`,
    subtitle: prop?.address || prop?.name || undefined,
  }
}

export function ConvertLeadModal({
  lead,
  open,
  onClose,
  onSuccess,
  onError,
  variant = 'default',
  confirmLabel,
}: ConvertLeadModalProps) {
  const [conversionData, setConversionData] = useState<ConversionData>(emptyConversionData())
  const [stepIndex, setStepIndex] = useState(0)
  const [converting, setConverting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [users, setUsers] = useState<UserProfile[]>([])

  const totalSteps = useMemo(
    () => CUSTOMER_STEPS.length + conversionData.properties.length * PROPERTY_TABS.length + 1,
    [conversionData.properties.length]
  )

  const isReviewStep = stepIndex === totalSteps - 1
  const propertyBlockStart = CUSTOMER_STEPS.length
  const propertyBlockSize = PROPERTY_TABS.length
  const isPropertyStep =
    stepIndex >= propertyBlockStart &&
    stepIndex < propertyBlockStart + conversionData.properties.length * propertyBlockSize
  const propertyIndex = isPropertyStep
    ? Math.floor((stepIndex - propertyBlockStart) / propertyBlockSize)
    : -1
  const propertyTab: PropertyTab | null = isPropertyStep
    ? PROPERTY_TABS[(stepIndex - propertyBlockStart) % propertyBlockSize]
    : null

  useEffect(() => {
    if (open && lead) {
      setConversionData(populateConversionDataFromLead(lead))
      setStepIndex(0)
      setError(null)
    }
  }, [open, lead])

  useEffect(() => {
    if (!open) return
    supabase
      .from('user_profiles')
      .select('id, first_name, last_name, email')
      .then(({ data }) => setUsers(data || []))
  }, [open])

  if (!open || !lead) return null

  const stepInfo = getStepLabel(stepIndex, conversionData.properties)

  const validateCurrentStep = (): boolean => {
    if (stepIndex === 0) {
      return !!conversionData.company_name.trim()
    }
    if (stepIndex === 1) {
      const primary = conversionData.contacts[0]
      return !!(primary?.first_name?.trim() || primary?.last_name?.trim())
    }
    if (isPropertyStep && propertyTab === 'address') {
      const prop = conversionData.properties[propertyIndex]
      if (!prop) return true
      return !!(prop.name?.trim() && prop.address?.trim() && prop.city?.trim() && prop.state?.trim() && prop.zip?.trim())
    }
    return true
  }

  const handleNext = () => {
    if (!validateCurrentStep()) {
      setError('Please fill in all required fields before continuing.')
      return
    }
    setError(null)
    if (stepIndex < totalSteps - 1) setStepIndex(stepIndex + 1)
  }

  const handleBack = () => {
    setError(null)
    if (stepIndex > 0) setStepIndex(stepIndex - 1)
  }

  const updateProperty = (index: number, patch: Partial<ConversionProperty>) => {
    setConversionData((prev) => ({
      ...prev,
      properties: prev.properties.map((p, i) => (i === index ? { ...p, ...patch } : p)),
    }))
  }

  const addProperty = () => {
    setConversionData((prev) => ({
      ...prev,
      properties: [...prev.properties, emptyConversionProperty()],
    }))
  }

  const removeProperty = (index: number) => {
    setConversionData((prev) => ({
      ...prev,
      properties: prev.properties.filter((_, i) => i !== index),
    }))
    if (isPropertyStep && propertyIndex >= index && stepIndex > propertyBlockStart) {
      setStepIndex(Math.max(propertyBlockStart, stepIndex - propertyBlockSize))
    }
  }

  const contactsAsCustomerContacts = (): CustomerContact[] =>
    conversionContactsToCustomerContacts(conversionData.contacts)

  const handleConfirm = async () => {
    try {
      setConverting(true)
      setError(null)
      await applyConversionDataToLead(lead.id, lead, conversionData)
      const customerId = await convertLeadToCustomer(lead.id)
      await onSuccess(customerId)
      onClose()
    } catch (err: any) {
      const message = err?.message || 'Failed to convert lead'
      setError(message)
      onError?.(message)
      try {
        await cleanupInternalConversionCustomFieldsOnLead(lead.id, lead)
      } catch (cleanupErr) {
        console.error('Failed to clean up internal conversion custom fields:', cleanupErr)
      }
    } finally {
      setConverting(false)
    }
  }

  const currentProperty = propertyIndex >= 0 ? conversionData.properties[propertyIndex] : null

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
      <div className="relative z-10 bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-6xl max-h-[95dvh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex justify-between items-start gap-3 p-4 sm:p-6 border-b border-gray-200 shrink-0">
          <div className="min-w-0">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">Convert Lead to Customer</h3>
            <p className="text-sm text-gray-500 mt-1">
              Step {stepIndex + 1} of {totalSteps} — {stepInfo.title}
              {stepInfo.subtitle ? ` (${stepInfo.subtitle})` : ''}
            </p>
            <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden w-full max-w-md">
              <div
                className="h-full bg-blue-600 transition-all duration-300"
                style={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }}
              />
            </div>
          </div>
          <button type="button" onClick={onClose} className={mobile.ghostBtn} aria-label="Close">
            ✕
          </button>
        </div>

        {variant === 'proposal' && stepIndex === 0 && (
          <div className="mx-6 mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg shrink-0">
            <p className="text-sm text-blue-800">
              <strong>This proposal is about to be approved.</strong> Complete each step to create the
              customer and properties before conversion.
            </p>
          </div>
        )}

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 shrink-0">
            {error}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {stepIndex === 0 && (
            <CustomerCompanyFields
              compact
              value={{
                name: conversionData.company_name,
                phone: conversionData.company_phone,
                email: conversionData.company_email,
                website: conversionData.company_website,
                address: conversionData.company_address,
                address_line_2: conversionData.company_address_line_2,
                city: conversionData.company_city,
                state: conversionData.company_state,
                zip: conversionData.company_zip,
              }}
              onChange={(v) =>
                setConversionData((prev) => ({
                  ...prev,
                  company_name: v.name,
                  company_phone: v.phone,
                  company_email: v.email,
                  company_website: v.website,
                  company_address: v.address,
                  company_address_line_2: v.address_line_2,
                  company_city: v.city,
                  company_state: v.state,
                  company_zip: v.zip,
                }))
              }
            />
          )}

          {stepIndex === 1 && (
            <CustomerContactsFields
              compact
              contacts={conversionData.contacts.map((c) => ({
                id: c.id,
                first_name: c.first_name,
                last_name: c.last_name,
                title: c.title,
                phone: c.phone,
                cell: c.cell,
                email: c.email,
              }))}
              onChange={(contacts) =>
                setConversionData((prev) => ({
                  ...prev,
                  contacts: contacts.map((c) => ({
                    id: c.id,
                    first_name: c.first_name,
                    last_name: c.last_name,
                    title: c.title || '',
                    phone: c.phone,
                    cell: c.cell,
                    email: c.email,
                  })),
                  contact_first_name: contacts[0]?.first_name || '',
                  contact_last_name: contacts[0]?.last_name || '',
                  email: contacts[0]?.email || '',
                  phone: contacts[0]?.phone || '',
                  cell: contacts[0]?.cell || '',
                }))
              }
            />
          )}

          {stepIndex === 2 && (
            <CustomerManagementFields
              compact
              management={{
                status: conversionData.status,
                assigned_to: conversionData.assigned_to,
                payment_method: conversionData.payment_method,
                sales_tax_status: conversionData.sales_tax_status,
              }}
              onManagementChange={(m) =>
                setConversionData((prev) => ({
                  ...prev,
                  status: m.status,
                  assigned_to: m.assigned_to,
                  payment_method: m.payment_method,
                  sales_tax_status: m.sales_tax_status,
                }))
              }
              customFields={conversionData.custom_fields}
              onCustomFieldsChange={(custom_fields) =>
                setConversionData((prev) => ({ ...prev, custom_fields }))
              }
              customerNotes={conversionData.customer_notes}
              onCustomerNotesChange={(customer_notes) =>
                setConversionData((prev) => ({ ...prev, customer_notes }))
              }
              users={users}
            />
          )}

          {isPropertyStep && currentProperty && propertyTab === 'address' && (
            <>
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-600">
                  {conversionData.properties.length} propert
                  {conversionData.properties.length === 1 ? 'y' : 'ies'} to configure
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={addProperty}
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <PlusIcon className="w-4 h-4" /> Add Property
                  </button>
                  {conversionData.properties.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeProperty(propertyIndex)}
                      className="px-3 py-1 text-sm text-red-600 border border-red-200 rounded-md hover:bg-red-50"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
              <PropertyAddressFields
                compact
                hideCustomerSelect
                value={{
                  customer_id: '',
                  name: currentProperty.name,
                  address: currentProperty.address,
                  address_line_2: currentProperty.address_line_2,
                  city: currentProperty.city,
                  state: currentProperty.state,
                  zip: currentProperty.zip,
                }}
                onChange={(v) =>
                  updateProperty(propertyIndex, {
                    name: v.name,
                    address: v.address,
                    address_line_2: v.address_line_2,
                    city: v.city,
                    state: v.state,
                    zip: v.zip,
                  })
                }
              />
            </>
          )}

          {isPropertyStep && currentProperty && propertyTab === 'info' && (
            <PropertyInfoFields
              compact
              value={{
                building_type: currentProperty.building_type,
                units: currentProperty.units,
                stories: currentProperty.stories,
                access_type: currentProperty.access_type,
                access_info: currentProperty.access_info,
              }}
              onChange={(info) => updateProperty(propertyIndex, info)}
              additionalAccess={currentProperty.additional_access}
              onAdditionalAccessChange={(additional_access) =>
                updateProperty(propertyIndex, { additional_access })
              }
            />
          )}

          {isPropertyStep && currentProperty && propertyTab === 'payment' && (
            <PropertyPaymentFields
              compact
              value={{
                payment_method: currentProperty.payment_method,
                sales_tax_status: currentProperty.sales_tax_status,
              }}
              onChange={(payment) => updateProperty(propertyIndex, payment)}
              customerDefaults={{
                payment_method: conversionData.payment_method || null,
                sales_tax_status: conversionData.sales_tax_status || null,
              }}
            />
          )}

          {isPropertyStep && currentProperty && propertyTab === 'contacts' && (
            <PropertyContactsFields
              compact
              conversionMode
              customerContacts={contactsAsCustomerContacts()}
              selectedContactIds={new Set(currentProperty.selected_contact_ids)}
              onSelectedContactIdsChange={(ids) =>
                updateProperty(propertyIndex, { selected_contact_ids: Array.from(ids) })
              }
              onAddContact={(contact) => {
                setConversionData((prev) => {
                  const exists = prev.contacts.some((c) => c.id === contact.id)
                  const contacts = exists ? prev.contacts : [...prev.contacts, {
                    id: contact.id,
                    first_name: contact.first_name,
                    last_name: contact.last_name,
                    title: contact.title || '',
                    phone: contact.phone,
                    cell: contact.cell,
                    email: contact.email,
                  }]
                  const props = prev.properties.map((p, i) =>
                    i === propertyIndex
                      ? { ...p, selected_contact_ids: [...p.selected_contact_ids, contact.id] }
                      : p
                  )
                  return { ...prev, contacts, properties: props }
                })
              }}
            />
          )}

          {isPropertyStep && currentProperty && propertyTab === 'notes' && (
            <PropertyNotesFields
              compact
              propertyNotes={currentProperty.property_notes}
              onPropertyNotesChange={(property_notes) =>
                updateProperty(propertyIndex, { property_notes })
              }
            />
          )}

          {conversionData.properties.length === 0 && stepIndex === propertyBlockStart && (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">No properties yet.</p>
              <button
                type="button"
                onClick={addProperty}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <PlusIcon className="w-4 h-4" /> Add Property
              </button>
            </div>
          )}

          {isReviewStep && (
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Customer</h4>
                <p className="text-sm text-gray-700">{conversionData.company_name || '—'}</p>
                <p className="text-sm text-gray-500">
                  {conversionData.contacts.length} contact(s) · Status: {conversionData.status}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">
                  Properties ({conversionData.properties.length})
                </h4>
                <ul className="space-y-2">
                  {conversionData.properties.map((p, i) => (
                    <li key={i} className="text-sm text-gray-700">
                      {p.name || `Property ${i + 1}`} — {p.address}
                      {p.city ? `, ${p.city}` : ''}
                    </li>
                  ))}
                  {conversionData.properties.length === 0 && (
                    <li className="text-sm text-gray-500 italic">No properties</li>
                  )}
                </ul>
              </div>
              <p className="text-sm text-gray-600">
                This will create a customer record, create property records, mark the lead as Won, and
                link related proposals.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center gap-2 p-4 sm:p-6 border-t border-gray-200 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className={mobile.secondaryBtn}
          >
            Cancel
          </button>
          <div className="flex gap-2 sm:gap-3">
            {stepIndex > 0 && (
              <button
                type="button"
                onClick={handleBack}
                className={mobile.secondaryBtn}
              >
                <ChevronLeftIcon className="w-4 h-4" /> Back
              </button>
            )}
            {isReviewStep ? (
              <button
                type="button"
                onClick={handleConfirm}
                disabled={converting}
                className={mobile.successBtn}
              >
                {converting
                  ? 'Converting…'
                  : (
                    <>
                      <span className="sm:hidden">Convert</span>
                      <span className="hidden sm:inline">
                        {confirmLabel || (variant === 'proposal' ? 'Convert & Continue' : 'Convert Lead')}
                      </span>
                    </>
                  )}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleNext}
                className={mobile.primaryBtn}
              >
                Next <ChevronRightIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
